import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CELLS = Array.from({ length: 60 });

export default function HeavyTransition({ onComplete }) {
  const [stage, setStage] = useState('core'); // core -> grid -> title -> out

  useEffect(() => {
    const t1 = setTimeout(() => setStage('grid'), 1300);
    const t2 = setTimeout(() => setStage('title'), 2900);
    const t3 = setTimeout(() => setStage('out'), 4400);
    const t4 = setTimeout(() => onComplete?.(), 5100);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [onComplete]);

  return (
    <AnimatePresence>
      {stage !== 'done' && (
        <motion.div
          className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: stage === 'out' ? 0 : 1 }}
          transition={{ duration: stage === 'out' ? 0.7 : 0.4 }}
          data-testid="heavy-transition-overlay"
        >
          {/* Power core */}
          <div className="relative flex items-center justify-center mb-10" style={{ height: 140 }}>
            <div className="heavy-core" />
            <div className="heavy-ring" />
            <div className="heavy-ring heavy-ring-2" />
            {CELLS.slice(0, 10).map((_, i) => (
              <span key={i} className="heavy-particle" style={{ '--pa': `${i * 36}deg`, animationDelay: `${i * 0.08}s` }} />
            ))}
          </div>

          {/* Shard grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: stage === 'core' ? 0 : 1, y: stage === 'core' ? 20 : 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-12 gap-1.5 mb-10"
            data-testid="heavy-transition-grid"
          >
            {CELLS.map((_, i) => (
              <span key={i} className={`heavy-cell ${stage !== 'core' ? 'heavy-cell-on' : ''}`} style={{ animationDelay: `${(i % 12) * 0.05 + Math.floor(i / 12) * 0.12}s` }} />
            ))}
          </motion.div>

          {/* Status text */}
          <div className="h-16 flex flex-col items-center justify-start">
            <AnimatePresence mode="wait">
              {stage === 'core' && (
                <motion.p key="s1" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="text-[11px] font-mono uppercase tracking-[0.4em] text-lime-400">
                  Initializing heavy core
                </motion.p>
              )}
              {stage === 'grid' && (
                <motion.p key="s2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="text-[11px] font-mono uppercase tracking-[0.4em] text-lime-400">
                  Orchestrating 60 models
                </motion.p>
              )}
              {(stage === 'title' || stage === 'out') && (
                <motion.div key="s3" initial={{ opacity: 0, scale: 0.85, letterSpacing: '0.4em' }}
                  animate={{ opacity: 1, scale: 1, letterSpacing: '0.06em' }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="text-center">
                  <h1 className="text-4xl sm:text-5xl font-bold heavy-gradient-text" style={{ fontFamily: 'Clash Display, sans-serif' }} data-testid="heavy-transition-title">
                    LINDEY HEAVY
                  </h1>
                  <p className="text-[10px] font-mono uppercase tracking-[0.45em] text-orange-400/80 mt-3">Maximum on-device power</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Flash */}
          {stage === 'title' && <div className="heavy-flash" />}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
